/**
 * Scheduler Formatting Utilities
 *
 * Utility functions for formatting scheduler data for display in UI.
 */

import type { ServiceArea, FranchiseRegion } from '../types'

/**
 * Format day boolean flags into readable string
 *
 * @example
 * formatDaysDisplay({ monday: true, tuesday: true, wednesday: false, ... })
 * // Returns: "Mon, Tue"
 */
export function formatDaysDisplay(area: ServiceArea): string {
  const days: Array<{ key: keyof ServiceArea; label: string }> = [
    { key: 'monday', label: 'Mon' },
    { key: 'tuesday', label: 'Tue' },
    { key: 'wednesday', label: 'Wed' },
    { key: 'thursday', label: 'Thu' },
    { key: 'friday', label: 'Fri' },
    { key: 'saturday', label: 'Sat' },
    { key: 'sunday', label: 'Sun' },
  ]

  const selectedDays = days.filter((day) => area[day.key] === true).map((day) => day.label)

  if (selectedDays.length === 0) {
    return 'No days selected'
  }

  // If all 7 days selected, show "Every Day"
  if (selectedDays.length === 7) {
    return 'Every Day'
  }

  // If weekdays only (Mon-Fri)
  if (
    selectedDays.length === 5 &&
    area.monday &&
    area.tuesday &&
    area.wednesday &&
    area.thursday &&
    area.friday &&
    !area.saturday &&
    !area.sunday
  ) {
    return 'Weekdays'
  }

  // If weekends only (Sat-Sun)
  if (selectedDays.length === 2 && area.saturday && area.sunday) {
    return 'Weekends'
  }

  return selectedDays.join(', ')
}

/**
 * Format date range for display
 *
 * @example
 * formatDateRange('2025-12-24', '2025-12-26')
 * // Returns: "Dec 24 - Dec 26, 2025"
 *
 * formatDateRange('2025-12-25', '2025-12-25')
 * // Returns: "Dec 25, 2025"
 */
export function formatDateRange(startDate: string, endDate: string): string {
  const start = new Date(startDate)
  const end = new Date(endDate)

  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
  }

  const yearOptions: Intl.DateTimeFormatOptions = {
    ...options,
    year: 'numeric',
  }

  // Same day - show once
  if (startDate === endDate) {
    return start.toLocaleDateString('en-US', yearOptions)
  }

  // Different days, same year
  if (start.getFullYear() === end.getFullYear()) {
    const startStr = start.toLocaleDateString('en-US', options)
    const endStr = end.toLocaleDateString('en-US', yearOptions)
    return `${startStr} - ${endStr}`
  }

  // Different years
  const startStr = start.toLocaleDateString('en-US', yearOptions)
  const endStr = end.toLocaleDateString('en-US', yearOptions)
  return `${startStr} - ${endStr}`
}

/**
 * Format date for input field (YYYY-MM-DD)
 */
export function formatDateForInput(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Get franchise region display label
 *
 * @example
 * getFranchiseLabel('hamilton')  // "Hamilton"
 * getFranchiseLabel('northyork') // "North York"
 */
export function getFranchiseLabel(region: FranchiseRegion | null): string {
  if (!region) return 'All Franchises'

  const labels: Record<FranchiseRegion, string> = {
    hamilton: 'Hamilton',
    northyork: 'North York',
  }

  return labels[region] || region
}

/**
 * Get franchise region short code
 *
 * @example
 * getFranchiseCode('hamilton')  // "HAM"
 * getFranchiseCode('northyork') // "NYK"
 */
export function getFranchiseCode(region: FranchiseRegion): string {
  const codes: Record<FranchiseRegion, string> = {
    hamilton: 'HAM',
    northyork: 'NYK',
  }

  return codes[region] || region.toUpperCase()
}

/**
 * Format timestamp to readable date/time
 *
 * @example
 * formatTimestamp('2025-12-06T10:30:00Z')
 * // Returns: "Dec 6, 2025 10:30 AM"
 */
export function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

/**
 * Format relative time (e.g., "2 days ago")
 */
export function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)

  if (diffSec < 60) return 'Just now'
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`
  if (diffHour < 24) return `${diffHour} hour${diffHour === 1 ? '' : 's'} ago`
  if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`

  return formatTimestamp(timestamp)
}

/**
 * Check if a date range overlaps with today
 */
export function isActiveNow(startDate: string, endDate: string): boolean {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const start = new Date(startDate)
  const end = new Date(endDate)

  return today >= start && today <= end
}

/**
 * Get status badge color based on active state
 */
export function getStatusColor(isActive: boolean): string {
  return isActive ? 'green' : 'gray'
}

/**
 * Get status label
 */
export function getStatusLabel(isActive: boolean): string {
  return isActive ? 'Active' : 'Inactive'
}

/**
 * Count active days in service area
 */
export function countActiveDays(area: ServiceArea): number {
  let count = 0
  if (area.monday) count++
  if (area.tuesday) count++
  if (area.wednesday) count++
  if (area.thursday) count++
  if (area.friday) count++
  if (area.saturday) count++
  if (area.sunday) count++
  return count
}

/**
 * Format duration between dates (in days)
 */
export function formatDuration(startDate: string, endDate: string): string {
  const start = new Date(startDate)
  const end = new Date(endDate)
  const diffMs = end.getTime() - start.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1 // +1 to include both start and end

  if (diffDays === 1) return '1 day'
  return `${diffDays} days`
}
